import type { SxProps } from '@mui/material'
import { DatePicker, type DatePickerProps, type PickersActionBarProps } from '@mui/x-date-pickers'
import type { Dayjs } from 'dayjs'
import { useField, useFormikContext } from 'formik'
import { defaultConversions, type Conversions } from './Conversions.js'

type Props<A> = Omit<DatePickerProps<Dayjs>, 'onChange' | 'value' | 'variant'> & {
  name: string
  variant?: 'outlined' | 'filled' | 'standard'
  required?: boolean
  conversions?: Conversions<A, Dayjs>
  fullWidth?: boolean
  sx?: SxProps
  size?: 'small' | 'medium'
  actionBarProps?: Omit<PickersActionBarProps, 'onAccept' | 'onClear' | 'onCancel' | 'onSetToday'>
  format?: string
}

export function DatePickerInput<A>({
  name,
  label,
  variant = 'outlined',
  // FIXME: look at the implementation better
  // @ts-ignore
  conversions = defaultConversions,
  fullWidth,
  required,

  sx = {},
  size,
  actionBarProps,
  format = 'DD-MM-YYYY',
  ...props
}: Props<A>): React.ReactElement {
  const { validateOnChange } = useFormikContext()
  const [field, meta, helpers] = useField<A | null>(name)

  const onChange = (date: Dayjs | null) => {
    helpers.setTouched(date ? date.isValid() : false, false)
    helpers.setValue(date ? conversions.from(date) : null, validateOnChange)
  }

  return (
    <DatePicker
      {...props}
      {...field}
      sx={sx}
      slotProps={{
        ...props.slotProps,
        textField: (ownerState) => {
          const textFieldProps =
            typeof props.slotProps?.textField === 'function'
              ? props.slotProps.textField(ownerState)
              : props.slotProps?.textField

          return {
            variant,
            name,
            size,
            required,
            id: `id-${name}`,
            fullWidth,
            'data-test': `datepicker-${name}`,
            ...textFieldProps,
            helperText: meta.touched && meta.error ? meta.error : textFieldProps?.helperText,
            error: !!meta.error && meta.touched,
          }
        },
        actionBar: {
          // @ts-expect-error
          'data-test': 'datepicker-dialog-actionbar',
          ...actionBarProps,
        },
      }}
      value={field.value ? conversions.to(field.value) : null}
      format={format}
      label={label}
      onChange={onChange}
    />
  )
}
